import React, { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { FeatureKey, FEATURE_UPGRADE_LABEL } from '../config/featureAccess';
import useAuthUser from '../services/useAuthUser';

interface FeatureAccessGateProps {
  children: React.ReactNode;
  feature: FeatureKey;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
  /** When true, case features show fallback (e.g. button) instead of the full sign-in card. Use in tight grids. */
  compact?: boolean;
  className?: string;
  onAccessDenied?: () => void;
}

export const FeatureAccessGate = ({ 
  children, 
  feature,
  fallback,
  showUpgradePrompt = true,
  compact = false,
  className = '',
  onAccessDenied = () => {}
}: FeatureAccessGateProps): ReactElement => {
  const navigate = useNavigate();
  const {
    loading,
    error,
    canAccess,
    isSubscriptionActive,
    refreshToken,
  } = useFeatureAccess();
  const { user } = useAuthUser();

  const handleUpgradeClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    navigate('/', { state: { scrollToPricing: true } });
    refreshToken();
  };

  if (loading) {
    return <div className="p-4 text-center">Loading...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-red-600">Error loading subscription status</div>;
  }

  if (canAccess(feature)) {
    return <>{children}</>;
  }

  if (!showUpgradePrompt) {
    return fallback ? <>{fallback}</> : <></>;
  }

  if (!isSubscriptionActive) {
    onAccessDenied();
  }

  // Case features (practice library): when not logged in, show sign-in for free access
  if (!user && feature.startsWith('case')) {
    if (compact && fallback) return <>{fallback}</>;
    return (
      <div className="min-h-[40vh] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100 py-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center border border-blue-100">
          <div className="bg-gradient-to-br from-blue-600 to-blue-400 rounded-full p-4 mb-4 shadow-lg">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign in to access the practice library</h2>
          <p className="text-gray-700 text-center mb-4">
            Create a free account to unlock all 17 Red Rock practice cases. No payment required.
          </p>
          <button
            onClick={() => navigate('/auth')}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg font-semibold rounded-xl shadow-md hover:from-blue-700 hover:to-blue-500 transition-all duration-300 mb-2"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const upgradeLabel = FEATURE_UPGRADE_LABEL[feature] ?? 'View plans';

  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-blue-100 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center border border-blue-100">
        <div className="bg-gradient-to-br from-blue-600 to-blue-400 rounded-full p-4 mb-4 shadow-lg">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium Feature Locked</h2>
        <p className="text-gray-700 text-center mb-4">
          This feature is available on a higher subscription tier.<br />
          Unlock access to advanced cases, tools, and exclusive content by upgrading your plan.
        </p>
        <button
          onClick={handleUpgradeClick}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-lg font-semibold rounded-xl shadow-md hover:from-blue-700 hover:to-blue-500 transition-all duration-300 mb-2"
        >
          {upgradeLabel}
          <svg className="w-5 h-5 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <div className="text-xs text-gray-500 mt-2 text-center">
          Already upgraded? <span className="underline cursor-pointer" onClick={refreshToken}>Refresh your access</span>
        </div>
      </div>
    </div>
  );
};